// ============================================================================
// apiFetch - authenticated drop-in replacement for `fetch('/api/...')`
// ============================================================================
// WHY THIS EXISTS
// ---------------
// Historically most of the SPA called `fetch('/api/...')` directly. Those
// requests carried NO Authorization header, so the backend could only identify
// the caller from the `x-ms-client-principal` header that the Static Web Apps
// linked-backend proxy injects. That header is forgeable by anyone who can
// reach the Function App directly, and PR #630 duly removed it as an
// authentication source - so a request without a bearer can no longer be
// authenticated at all. `apiFetch` therefore refuses to send one, returning
// the same structured 401 the API would (see unauthenticatedResponse below).
//
// `apiFetch` is deliberately a *drop-in* for `fetch`: it returns the raw
// `Response` and NEVER throws on a non-2xx status. That keeps every existing
// call site's error handling (`if (!res.ok)`, `r.ok ? r.json() : null`,
// `res.blob()`, silent `.catch()`) byte-for-byte identical. The typed `api`
// client in ./api.ts throws `ApiError` on non-ok, which is a different
// contract - do not swap one for the other mechanically.
//
// The only behavioural difference vs. `fetch` is the added Authorization
// header (and only for same-origin /api targets).
// ============================================================================

/** Resolves the bearer token for outgoing /api calls, or null when signed out. */
export type TokenResolver = () => Promise<string | null>;

/**
 * True when `input` resolves to a same-origin `/api/...` URL. We refuse to
 * attach the bearer token to anything else so a token can never leak to a
 * third-party origin via a mis-typed or attacker-influenced URL.
 *
 * Exported for scripts/test-api-fetch.mjs - not part of the public surface.
 */
export function isSameOriginApiTarget(input: string): boolean {
  const base =
    typeof window !== 'undefined' && window.location
      ? window.location.origin
      : 'http://localhost';
  let url: URL;
  try {
    url = new URL(input, base);
  } catch {
    return false;
  }
  if (url.origin !== base) return false;
  return url.pathname === '/api' || url.pathname.startsWith('/api/');
}

/** Production resolver: lazy-imports MSAL so this module stays usable in Node. */
const msalTokenResolver: TokenResolver = async () => {
  try {
    const { getBearer, startInteractiveAuth } = await import(
      '../lib/auth/msalConfig'
    );
    const { token, reason } = await getBearer();
    if (token) {
      return token;
    }
    // No token. When there IS a session but it could not be reused silently,
    // escalate to an interactive MSAL sign-in (guarded to fire once). Not
    // awaited: it is a full-page redirect, and awaiting it would stall this
    // request behind a navigation that discards it anyway.
    void startInteractiveAuth(reason);
    return null;
  } catch {
    // MSAL module unavailable (not configured, or running outside a browser).
    return null;
  }
};

let tokenResolver: TokenResolver = msalTokenResolver;

/**
 * Test seam. Replaces the token source so scripts/test-api-fetch.mjs can prove
 * that a resolved token actually reaches `globalThis.fetch` THROUGH apiFetch.
 * Without this, MSAL is absent under Node, every token resolves to null, and
 * the auth branch is never exercised. Pass null to restore the MSAL resolver.
 *
 * Not referenced by any production code path.
 */
export function __setTokenResolverForTests(fn: TokenResolver | null): void {
  tokenResolver = fn ?? msalTokenResolver;
}

// --- Observability (frontend-only, zero behaviour change) -------------------
// Lets Zach smoke-test bearer coverage instead of silently falling through to
// the forgeable x-ms-client-principal header. Counters only: no tokens, no
// PII, no URLs, no request bodies.
//
// Read during smoke testing with:  window.__apiFetchAuthStats
export interface ApiFetchAuthStats {
  /** /api calls that went out WITH an Authorization: Bearer header. */
  bearer: number;
  /** /api calls refused locally because no bearer was available. */
  none: number;
}

function isDev(): boolean {
  try {
    return !!(import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV;
  } catch {
    return false;
  }
}

function recordAuthOutcome(hasBearer: boolean): void {
  if (typeof window === 'undefined') return;
  const w = window as unknown as { __apiFetchAuthStats?: ApiFetchAuthStats };
  const stats = w.__apiFetchAuthStats ?? { bearer: 0, none: 0 };
  if (hasBearer) stats.bearer++;
  else stats.none++;
  w.__apiFetchAuthStats = stats;

  if (!hasBearer && isDev()) {
    // No URL: keeps query strings (which can carry customer names) out of logs.
    console.warn(
      '[apiFetch] no bearer token available for a same-origin /api call - ' +
      'the request was NOT sent; a local 401 was returned instead. Since PR ' +
      '#630 the x-ms-client-principal fallback no longer authenticates, so an ' +
      'unauthenticated call could only 401 server-side anyway. ' +
      'See window.__apiFetchAuthStats.',
    );
  }
}

/**
 * Merge the bearer token into the caller's headers.
 *
 * Rules, all load-bearing:
 *  - a caller-supplied `Authorization` always wins; we never overwrite it
 *  - a null/empty token changes nothing
 *  - we never inject `Content-Type`, so `FormData` bodies keep the browser's
 *    multipart boundary and bare GETs stay header-free
 *
 * Exported for scripts/test-api-fetch.mjs - not part of the public surface.
 */
export function buildAuthHeaders(
  init: HeadersInit | undefined,
  token: string | null,
): Headers {
  const headers = new Headers(init);
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return headers;
}

/**
 * Authenticated `fetch` for the SPA's own `/api/*` endpoints.
 *
 * Identical semantics to `fetch(input, init)` - same return value, same
 * rejection behaviour (network/abort errors reject; HTTP error statuses do
 * not) - plus an `Authorization: Bearer <token>` header when a token is
 * available and the target is a same-origin `/api` path.
 *
 * A caller-supplied `Authorization` header always wins; we never overwrite it.
 * We also never inject `Content-Type`, so `FormData` bodies still get the
 * browser's multipart boundary.
 */
export async function apiFetch(input: string, init: RequestInit = {}): Promise<Response> {
  if (!isSameOriginApiTarget(input)) {
    // Not ours - behave exactly like a bare fetch, with no token attached.
    return fetch(input, init);
  }

  const callerHeaders = init.headers as HeadersInit | undefined;
  // Only pay for token acquisition when we would actually use it.
  const alreadyAuthed = new Headers(callerHeaders).has('Authorization');
  const token = alreadyAuthed ? null : await tokenResolver();
  const headers = buildAuthHeaders(callerHeaders, token);

  const hasBearer = headers.has('Authorization');
  recordAuthOutcome(hasBearer);

  // Do not send a staff-gated /api call with no Authorization header.
  //
  // Before PR #630 such a request was still authenticated, by the
  // x-ms-client-principal header the SWA proxy injects. That path is gone, so
  // an unauthenticated call can now only ever come back 401 - it cannot
  // succeed, and firing it converts a recoverable auth state into a failed
  // user action (a lost handoff draft, an empty list). Short-circuit with the
  // same structured 401 the API itself would return, so every existing call
  // site's `if (!res.ok)` handling behaves exactly as it does today.
  if (!hasBearer) return unauthenticatedResponse();

  return fetch(input, { ...init, headers });
}

/**
 * The 401 the API would have returned, synthesised locally.
 *
 * Body shape mirrors buildErrorBody() in api/shared/errors.ts so callers that
 * read `body.error.code` see the same `AUTH_UNAUTHORIZED` they would from the
 * server. apiFetch's contract is preserved: a Response is returned, nothing
 * throws, and non-2xx is not an exception.
 *
 * EXPORTED because the typed client in ./api.ts needs the identical response
 * for its own no-bearer short-circuit. Two hand-written copies of this would
 * be free to drift, and a half-applied auth rule across two request paths is
 * exactly the failure this whole change is repairing - api.ts and apiFetch.ts
 * must refuse an unauthenticated staff-gated call the same way.
 */
export function unauthenticatedResponse(): Response {
  return new Response(
    JSON.stringify({
      error: {
        code: 'AUTH_UNAUTHORIZED',
        message: 'Not signed in. Complete sign-in and retry.',
        status: 401,
      },
    }),
    { status: 401, headers: { 'Content-Type': 'application/json' } },
  );
}

export default apiFetch;
